"use client";

import { motion } from "framer-motion";
import { BgLayer } from "./bg/BgPrimitives";

export default function WhyItMatters() {
  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };
  const cardVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number] } },
  };

  return (
    <section id="why" className="bg-background py-20 px-4 relative overflow-hidden section-grain text-foreground">
      <BgLayer>
        {/* halftone — fades from dense bottom-right to sparse */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(circle, var(--color-primary) 1.6px, transparent 1.8px)",
            backgroundSize: "22px 22px",
            opacity: 0.18,
            WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 100% 100%, black 0%, transparent 70%)",
            maskImage: "radial-gradient(ellipse 80% 70% at 100% 100%, black 0%, transparent 70%)",
          }}
        />
        {/* Ticker bar */}
        <div
          style={{
            position: "absolute", top: "4%", left: "-10%", right: "-10%",
            height: "26px", background: "var(--foreground)",
            transform: "rotate(-2deg)", display: "flex", alignItems: "center",
            overflow: "hidden", opacity: 0.95,
          }}
        >
          <div
            style={{
              display: "flex", whiteSpace: "nowrap",
              animation: "bgTicker 30s linear infinite",
              color: "#ffffff", fontSize: "11px", fontWeight: 700, letterSpacing: "0.18em",
            }}
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <span key={i} style={{ padding: "0 28px" }}>SAVE • EARN • SPEND • INVEST • LEVEL UP •</span>
            ))}
          </div>
        </div>
      </BgLayer>
      <div className="container mx-auto max-w-5xl relative z-10">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Why this matters to you
          </h2>
          <p className="text-foreground/70 text-base md:text-lg leading-relaxed max-w-3xl">
            Money decisions are already part of your life, whether you are managing transport and
            daily spending, shopping online, freelancing, saving for something important, or thinking
            about your future career or business. This program helps you understand how money works in
            real life so you can make better choices now, avoid costly mistakes, and build habits that
            support your future.
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {/* Card 1 — mint accent stripe */}
          <motion.div
            className="bg-muted border border-muted/60 rounded-xl overflow-hidden hover:border-primary transition-colors cursor-default"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <div className="h-1.5 bg-primary w-full" />
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Make smarter decisions now
              </h3>
              <p className="text-sm text-foreground/70 leading-relaxed">
                Learn how to handle everyday money choices with more confidence, from spending and
                saving to online purchases and managing what comes in and goes out.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="bg-muted border border-muted/60 rounded-xl overflow-hidden hover:border-primary transition-colors cursor-default"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <div className="h-1.5 bg-primary w-full" />
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Plan ahead with more confidence
              </h3>
              <p className="text-sm text-foreground/70 leading-relaxed">
                Understand how today&apos;s decisions affect your future, whether you are saving for a
                goal, starting work, freelancing, or thinking about your next move.
              </p>
            </div>
          </motion.div>

          <motion.div
            className="bg-muted border border-muted/60 rounded-xl overflow-hidden hover:border-primary transition-colors cursor-default"
            variants={cardVariants}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
          >
            <div className="h-1.5 bg-primary w-full" />
            <div className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Build skills you will keep using
              </h3>
              <p className="text-sm text-foreground/70 leading-relaxed">
                These are practical skills for real life — useful whether you want to manage your own
                money better, grow a side income, become an entrepreneur, or simply feel more in
                control.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
